import React, { useState, useRef, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  HomeIcon,
  ServerIcon,
  NetworkIcon,
  DatabaseIcon,
  CloudIcon,
  ActivityIcon,
  ShieldIcon,
  GridIcon,
} from "../Components/icons/ProductIcons";

// Products launcher menu (grid icon → dropdown). "Home" is the landing site itself —
// an internal route (Link), always shown as the selected/current item, and kept on
// its own row above the grid rather than sharing a column with the product tiles.
// Everything below is an external link, split into two tiers by the divider line.
const HOME_ITEM = { name: "Home", icon: HomeIcon, path: "/", selected: true };
const CORE_PRODUCTS = [
  { name: "Nodegate", icon: ServerIcon, href: "https://nodegate.nodefleet.net/" },
  { name: "Datanodes", icon: NetworkIcon, href: "https://datanodes.nodefleet.net/" },
  { name: "Datahub", icon: DatabaseIcon, href: "https://datahub.nodefleet.net" },
];
const APP_PRODUCTS = [
  { name: "FleetWallet", icon: CloudIcon, href: "https://fleetwallet.nodefleet.net/" },
  { name: "PoktAi", icon: ActivityIcon, href: "https://pokt.ai/" },
  { name: "AIViber", icon: ShieldIcon, href: "https://aiviber.io/" },
  { name: "PoktPool", icon: CloudIcon, href: "https://poktpool.com/" },
  { name: "Botfleet", icon: CloudIcon, href: "https://botfleet.net/" },
];

/** A single tile in the products launcher — internal route (Home) uses Link, everything
 *  else is an external product site. `selected` pins the highlighted style permanently
 *  (current page) instead of only on hover. `variant="row"` renders it as a standalone
 *  full-width line (used for Home, kept out of the 3-column grid) instead of a grid tile. */
function ProductTile({ product, onNavigate, variant = "tile" }) {
  const Icon = product.icon;
  const isRow = variant === "row";
  // group-hover (not hover:) so the whole tile — icon AND label — is the hover
  // target, not just the 64px icon square itself.
  const iconClasses = `flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border-2 text-white transition-colors ${
    product.selected
      ? "border-primary bg-primary/20 text-primary shadow-glow-sm"
      : "border-white/15 bg-white/5 group-hover:border-primary group-hover:bg-primary/20 group-hover:text-primary group-hover:shadow-glow-sm"
  }`;
  const content = isRow ? (
    <>
      <span className={iconClasses}>
        <Icon className="h-6 w-6" />
      </span>
      <span className="text-base font-medium text-gray-200">{product.name}</span>
    </>
  ) : (
    <>
      <span className={iconClasses}>
        <Icon className="h-6 w-6" />
      </span>
      <span className="text-sm text-gray-200">{product.name}</span>
    </>
  );
  const className = isRow
    ? "group flex items-center gap-4 rounded-2xl px-2 py-1 no-underline"
    : "group flex flex-col items-center gap-2 text-center no-underline";

  if (product.path) {
    return (
      <Link
        to={product.path}
        onClick={onNavigate}
        aria-current={product.selected ? "page" : undefined}
        className={className}
      >
        {content}
      </Link>
    );
  }

  return (
    <a
      href={product.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
      className={className}
    >
      {content}
    </a>
  );
}

const Navbar = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  // Article pages have no #products/#services/#team sections to scroll to,
  // so the nav is stripped down to just the logo there.
  const isArticlePage = location.pathname.startsWith("/articles");
  const [open, setOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const productsMenuRef = useRef(null);

  // Close the products launcher on outside click / Escape.
  useEffect(() => {
    if (!productsMenuOpen) return;
    const handleClick = (e) => {
      if (productsMenuRef.current && !productsMenuRef.current.contains(e.target)) {
        setProductsMenuOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setProductsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [productsMenuOpen]);

  // In-page sections (rebrand 2026 nav).
  const Menu = [
    { name: "Products", hash: "#products" },
    { name: "Networks", hash: "#networks" },
    { name: "Services", hash: "#services" },
    { name: "Team", hash: "#team" },
    { name: "Security Assessments", path: "/audit" },
  ];

  const handleNavigation = (e, hash) => {
    setOpen(false);
    if (isHome && hash) {
      e.preventDefault();
      const target = document.querySelector(hash);
      if (target) {
        window.scrollTo({ top: target.offsetTop, behavior: "smooth" });
      }
    }
  };

  return (
    <div>
      <div className="relative" id="Home">
        <header className="absolute left-0 top-0 z-50 w-full">
          <div className="flex w-full items-center justify-between px-8 py-7 max-sm:px-6 lg:px-20">
            <Link
              to="/"
              className="relative z-50"
              onClick={() => setOpen(false)}
            >
              <img
                src="/assets/icons/Logo_Nodefleet.svg"
                alt="Nodefleet"
                className="h-7 w-auto"
              />
            </Link>

            {!isArticlePage && (
              <>
                {/* Mobile toggle */}
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  aria-label="Toggle menu"
                  className="z-50 hidden border-0 bg-transparent text-2xl text-white max-sm:block"
                >
                  <i className={`fas ${open ? "fa-times" : "fa-bars"}`} />
                </button>

                {/* Links */}
                <nav
                  className={`flex items-center gap-9 max-sm:absolute max-sm:right-0 max-sm:top-full max-sm:w-full max-sm:flex-col max-sm:items-end max-sm:gap-6 max-sm:bg-space-deep/95 max-sm:p-6 max-sm:backdrop-blur ${
                    open ? "max-sm:flex" : "max-sm:hidden"
                  }`}
                >
                  {Menu.map(({ name, hash, path }) => (
                    <Link
                      key={name}
                      to={path || (isHome ? hash : `/${hash}`)}
                      onClick={(e) => handleNavigation(e, path ? undefined : hash)}
                      className="font-medium text-gray-200 no-underline transition-colors hover:text-white"
                    >
                      {name}
                    </Link>
                  ))}
                  <a
                    href="https://t.me/nodefleet"
                    className="rounded-full bg-primary px-6 py-2.5 font-semibold text-space-deep no-underline shadow-glow-sm transition-colors hover:bg-primary-400"
                  >
                    Join the Future
                  </a>

                  {/* Products launcher — grid icon opens a dropdown with every Nodefleet product. */}
                  <div className="relative" ref={productsMenuRef}>
                    <button
                      type="button"
                      onClick={() => setProductsMenuOpen((v) => !v)}
                      aria-haspopup="true"
                      aria-expanded={productsMenuOpen}
                      aria-label="Nodefleet products"
                      className={`flex h-11 w-11 items-center justify-center rounded-full border-2 transition-colors ${
                        productsMenuOpen
                          ? "border-primary bg-primary/20 text-primary shadow-glow-sm"
                          : "border-white/25 text-white hover:border-primary hover:bg-primary/20 hover:text-primary hover:shadow-glow-sm"
                      }`}
                    >
                      <GridIcon className="h-5 w-5" />
                    </button>

                    <AnimatePresence>
                      {productsMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                          className="absolute right-0 top-full mt-4 w-[380px] rounded-3xl border border-white/10 bg-space-card/95 p-8 shadow-2xl backdrop-blur-md max-sm:w-[320px]"
                        >
                          <p className="mb-6 text-lg font-semibold text-white">
                            Nodefleet products
                          </p>

                          <div className="mb-6 border-b border-white/10 pb-6">
                            <ProductTile
                              product={HOME_ITEM}
                              variant="row"
                              onNavigate={() => setProductsMenuOpen(false)}
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-x-4 gap-y-6">
                            {CORE_PRODUCTS.map((p) => (
                              <ProductTile
                                key={p.name}
                                product={p}
                                onNavigate={() => setProductsMenuOpen(false)}
                              />
                            ))}

                            <div className="col-span-3 border-t border-accent/40" />

                            {APP_PRODUCTS.map((p) => (
                              <ProductTile
                                key={p.name}
                                product={p}
                                onNavigate={() => setProductsMenuOpen(false)}
                              />
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </nav>
              </>
            )}
          </div>
        </header>

        {/* flow-root: pages like Blog/BlogPost/Articles/ArticleDetail use a
            negative top margin (-mt-24) on their root element so the hero
            fills in behind the transparent header. Without a block
            formatting context here, that negative margin collapses through
            this <section> and #Home too, shifting the *header's own*
            containing block up by the same amount and pushing it off-screen. */}
        <section className="flow-root">
          <Outlet />
        </section>
      </div>
    </div>
  );
};

export default Navbar;
